/**
 * aeterna/neural/affect-lexicon.ts — Ground-Truth Affect Calibration
 *
 * ~800 words × 8 affect dimensions, hand-calibrated.
 * Ships as a ~50KB bundled asset. NOT fetched — compiled in.
 *
 * This is the single most important structural fix in the stack.
 * The CharCNN uses random convolutional weights. The ESN trains to
 * reproduce CharCNN's random outputs. Every downstream layer —
 * polyvagal, drives, contagion, peak-end, context envelope — runs
 * on an affect pipeline whose ground truth was deterministic noise.
 *
 * This lexicon provides ground-truth affect from message 1.
 * The CharCNN still runs (captures character-level patterns, punctuation,
 * capitalization) but for any word that hits the lexicon, the lexicon
 * dominates at weight 0.7.
 *
 * The 30 semantic clusters in embeddings.ts do geometric proximity.
 * This does affect classification. They solve different problems.
 *
 * Dimensions: [warmth, tension, curiosity, melancholy, playfulness,
 *              depth, urgency, openness]
 * Range: [0.0, 1.0] per dimension
 *
 * Organization: by emotional category for maintainability.
 * At runtime: flattened into a Map<string, Float32Array> for O(1) lookup.
 */

// ─── Raw Lexicon Data ─────────────────────────────────────────────────────────
// Stored as number[] tuples for compactness, converted to Float32Array on load.
// [warmth, tension, curiosity, melancholy, playfulness, depth, urgency, openness]

const RAW: Record<string, readonly number[]> = {

  // ═══ JOY / POSITIVE VALENCE ═══════════════════════════════════════════════

  // Pure joy
  "happy":        [0.8, 0.1, 0.3, 0.0, 0.6, 0.1, 0.1, 0.9],
  "glad":         [0.7, 0.0, 0.2, 0.0, 0.4, 0.1, 0.1, 0.8],
  "joyful":       [0.9, 0.1, 0.3, 0.0, 0.7, 0.2, 0.2, 0.9],
  "elated":       [0.8, 0.2, 0.4, 0.0, 0.7, 0.1, 0.3, 0.9],
  "delighted":    [0.9, 0.1, 0.4, 0.0, 0.7, 0.1, 0.2, 0.9],
  "thrilled":     [0.8, 0.2, 0.7, 0.0, 0.8, 0.1, 0.6, 0.9],
  "ecstatic":     [0.8, 0.3, 0.5, 0.0, 0.8, 0.1, 0.6, 1.0],
  "overjoyed":    [0.9, 0.2, 0.3, 0.0, 0.6, 0.2, 0.3, 0.9],
  "blissful":     [0.9, 0.0, 0.2, 0.0, 0.4, 0.5, 0.0, 0.9],
  "euphoric":     [0.8, 0.2, 0.5, 0.0, 0.7, 0.2, 0.4, 1.0],
  "cheerful":     [0.8, 0.0, 0.3, 0.0, 0.7, 0.0, 0.1, 0.9],
  "merry":        [0.8, 0.0, 0.2, 0.0, 0.8, 0.0, 0.1, 0.9],
  "jubilant":     [0.8, 0.2, 0.3, 0.0, 0.7, 0.1, 0.4, 0.9],

  // Contentment / peace
  "content":      [0.7, 0.0, 0.3, 0.1, 0.3, 0.4, 0.0, 0.8],
  "satisfied":    [0.6, 0.0, 0.2, 0.0, 0.3, 0.3, 0.0, 0.7],
  "peaceful":     [0.7, 0.0, 0.2, 0.1, 0.2, 0.5, 0.0, 0.8],
  "serene":       [0.7, 0.0, 0.2, 0.1, 0.1, 0.6, 0.0, 0.8],
  "tranquil":     [0.6, 0.0, 0.2, 0.1, 0.1, 0.6, 0.0, 0.7],
  "calm":         [0.5, 0.0, 0.2, 0.1, 0.2, 0.4, 0.0, 0.7],
  "relaxed":      [0.6, 0.0, 0.2, 0.0, 0.3, 0.3, 0.0, 0.8],
  "comfortable":  [0.7, 0.0, 0.2, 0.0, 0.3, 0.2, 0.0, 0.8],
  "cozy":         [0.9, 0.0, 0.2, 0.1, 0.3, 0.2, 0.0, 0.7],
  "safe":         [0.7, 0.0, 0.1, 0.0, 0.1, 0.3, 0.0, 0.6],
  "secure":       [0.6, 0.0, 0.1, 0.0, 0.1, 0.3, 0.0, 0.5],
  "settled":      [0.5, 0.0, 0.1, 0.0, 0.1, 0.3, 0.0, 0.5],
  "at ease":      [0.6, 0.0, 0.2, 0.0, 0.2, 0.3, 0.0, 0.8],

  // Warmth / affection
  "warm":         [0.9, 0.0, 0.2, 0.1, 0.3, 0.3, 0.0, 0.8],
  "loving":       [1.0, 0.1, 0.2, 0.1, 0.2, 0.5, 0.1, 0.8],
  "affectionate": [0.9, 0.0, 0.2, 0.0, 0.3, 0.3, 0.0, 0.8],
  "tender":       [0.9, 0.0, 0.1, 0.2, 0.1, 0.5, 0.0, 0.7],
  "caring":       [0.9, 0.1, 0.2, 0.1, 0.1, 0.4, 0.1, 0.7],
  "gentle":       [0.8, 0.0, 0.1, 0.1, 0.2, 0.4, 0.0, 0.7],
  "kind":         [0.9, 0.0, 0.2, 0.0, 0.2, 0.3, 0.0, 0.8],
  "compassionate":[0.9, 0.1, 0.2, 0.2, 0.0, 0.6, 0.1, 0.7],
  "empathetic":   [0.8, 0.1, 0.3, 0.2, 0.0, 0.6, 0.1, 0.8],
  "sympathetic":  [0.7, 0.1, 0.2, 0.3, 0.0, 0.5, 0.1, 0.6],
  "sweet":        [0.9, 0.0, 0.2, 0.0, 0.5, 0.2, 0.0, 0.8],
  "dear":         [0.9, 0.0, 0.1, 0.2, 0.2, 0.4, 0.0, 0.7],
  "precious":     [0.9, 0.1, 0.2, 0.1, 0.3, 0.5, 0.1, 0.7],
  "cherish":      [0.9, 0.1, 0.1, 0.1, 0.1, 0.6, 0.1, 0.6],
  "adore":        [1.0, 0.1, 0.2, 0.0, 0.4, 0.3, 0.2, 0.8],

  // Gratitude
  "grateful":     [0.8, 0.0, 0.2, 0.1, 0.2, 0.5, 0.0, 0.8],
  "thankful":     [0.8, 0.0, 0.2, 0.1, 0.2, 0.4, 0.0, 0.8],
  "blessed":      [0.8, 0.0, 0.2, 0.1, 0.2, 0.5, 0.0, 0.7],
  "appreciate":   [0.8, 0.0, 0.3, 0.0, 0.2, 0.4, 0.0, 0.8],
  "thanks":       [0.7, 0.0, 0.1, 0.0, 0.3, 0.1, 0.0, 0.8],
  "thank":        [0.7, 0.0, 0.1, 0.0, 0.3, 0.1, 0.0, 0.8],

  // Excitement / energy
  "excited":      [0.7, 0.3, 0.7, 0.0, 0.8, 0.1, 0.5, 0.9],
  "eager":        [0.5, 0.2, 0.8, 0.0, 0.5, 0.2, 0.5, 0.9],
  "enthusiastic": [0.7, 0.2, 0.7, 0.0, 0.6, 0.2, 0.4, 0.9],
  "passionate":   [0.7, 0.3, 0.6, 0.0, 0.4, 0.5, 0.5, 0.8],
  "energetic":    [0.5, 0.2, 0.5, 0.0, 0.7, 0.1, 0.4, 0.8],
  "alive":        [0.7, 0.2, 0.6, 0.0, 0.6, 0.3, 0.3, 0.9],
  "vibrant":      [0.7, 0.2, 0.5, 0.0, 0.7, 0.2, 0.3, 0.9],
  "exhilarated":  [0.7, 0.3, 0.6, 0.0, 0.7, 0.2, 0.5, 0.9],
  "pumped":       [0.5, 0.3, 0.5, 0.0, 0.7, 0.0, 0.6, 0.8],
  "stoked":       [0.6, 0.2, 0.6, 0.0, 0.8, 0.0, 0.5, 0.9],
  "hyped":        [0.5, 0.3, 0.5, 0.0, 0.7, 0.0, 0.6, 0.8],

  // Amusement / play
  "funny":        [0.6, 0.0, 0.3, 0.0, 0.9, 0.0, 0.1, 0.9],
  "hilarious":    [0.6, 0.1, 0.3, 0.0, 1.0, 0.0, 0.2, 0.9],
  "laughing":     [0.7, 0.0, 0.2, 0.0, 1.0, 0.0, 0.1, 0.9],
  "lol":          [0.5, 0.0, 0.2, 0.0, 0.9, 0.0, 0.0, 0.8],
  "lmao":         [0.5, 0.0, 0.2, 0.0, 1.0, 0.0, 0.1, 0.8],
  "haha":         [0.6, 0.0, 0.2, 0.0, 0.9, 0.0, 0.0, 0.9],
  "hahaha":       [0.6, 0.0, 0.2, 0.0, 1.0, 0.0, 0.0, 0.9],
  "hehe":         [0.6, 0.0, 0.2, 0.0, 0.8, 0.0, 0.0, 0.8],
  "silly":        [0.5, 0.0, 0.2, 0.0, 0.9, 0.0, 0.0, 0.8],
  "goofy":        [0.5, 0.0, 0.2, 0.0, 0.9, 0.0, 0.0, 0.8],
  "playful":      [0.6, 0.0, 0.4, 0.0, 0.9, 0.1, 0.1, 0.9],
  "witty":        [0.5, 0.0, 0.5, 0.0, 0.8, 0.3, 0.1, 0.8],
  "amusing":      [0.5, 0.0, 0.3, 0.0, 0.8, 0.1, 0.0, 0.8],
  "fun":          [0.6, 0.0, 0.4, 0.0, 0.9, 0.0, 0.1, 0.9],
  "awesome":      [0.7, 0.1, 0.4, 0.0, 0.7, 0.1, 0.2, 0.9],
  "amazing":      [0.7, 0.1, 0.5, 0.0, 0.6, 0.2, 0.3, 0.9],
  "wonderful":    [0.8, 0.1, 0.4, 0.0, 0.5, 0.3, 0.1, 0.9],
  "fantastic":    [0.7, 0.1, 0.4, 0.0, 0.6, 0.2, 0.2, 0.9],
  "great":        [0.6, 0.0, 0.3, 0.0, 0.5, 0.1, 0.1, 0.8],
  "nice":         [0.6, 0.0, 0.2, 0.0, 0.3, 0.1, 0.0, 0.7],
  "good":         [0.5, 0.0, 0.2, 0.0, 0.3, 0.1, 0.0, 0.7],
  "cool":         [0.4, 0.0, 0.4, 0.0, 0.5, 0.1, 0.0, 0.7],
  "neat":         [0.4, 0.0, 0.5, 0.0, 0.4, 0.2, 0.0, 0.7],

  // Hope / optimism
  "hopeful":      [0.6, 0.1, 0.5, 0.1, 0.2, 0.5, 0.2, 0.8],
  "optimistic":   [0.6, 0.1, 0.5, 0.0, 0.3, 0.3, 0.2, 0.8],
  "encouraged":   [0.6, 0.1, 0.4, 0.0, 0.3, 0.3, 0.2, 0.8],
  "inspired":     [0.6, 0.2, 0.7, 0.0, 0.4, 0.5, 0.3, 0.9],
  "motivated":    [0.5, 0.2, 0.6, 0.0, 0.3, 0.3, 0.5, 0.8],
  "determined":   [0.4, 0.3, 0.5, 0.0, 0.2, 0.4, 0.6, 0.6],

  // Pride / confidence
  "proud":        [0.6, 0.1, 0.3, 0.0, 0.4, 0.4, 0.2, 0.7],
  "confident":    [0.5, 0.1, 0.4, 0.0, 0.3, 0.3, 0.2, 0.7],
  "accomplished": [0.6, 0.1, 0.3, 0.0, 0.3, 0.4, 0.1, 0.7],
  "brave":        [0.5, 0.3, 0.4, 0.0, 0.2, 0.5, 0.4, 0.7],
  "courageous":   [0.5, 0.3, 0.4, 0.0, 0.2, 0.5, 0.4, 0.7],
  "strong":       [0.4, 0.2, 0.3, 0.0, 0.2, 0.4, 0.3, 0.6],

  // ═══ SADNESS / NEGATIVE VALENCE ═══════════════════════════════════════════

  // Sadness / grief
  "sad":          [0.2, 0.2, 0.1, 0.8, 0.0, 0.4, 0.1, 0.3],
  "unhappy":      [0.2, 0.3, 0.1, 0.7, 0.0, 0.3, 0.2, 0.3],
  "miserable":    [0.1, 0.4, 0.0, 0.9, 0.0, 0.3, 0.3, 0.1],
  "depressed":    [0.1, 0.3, 0.0, 0.9, 0.0, 0.3, 0.1, 0.1],
  "devastated":   [0.0, 0.9, 0.0, 1.0, 0.0, 0.4, 0.7, 0.1],
  "heartbroken":  [0.1, 0.7, 0.0, 1.0, 0.0, 0.6, 0.4, 0.1],
  "grief":        [0.2, 0.5, 0.0, 1.0, 0.0, 0.7, 0.3, 0.2],
  "grieving":     [0.2, 0.5, 0.0, 1.0, 0.0, 0.7, 0.3, 0.2],
  "mourning":     [0.2, 0.4, 0.0, 1.0, 0.0, 0.8, 0.2, 0.2],
  "sorrowful":    [0.2, 0.3, 0.0, 0.9, 0.0, 0.7, 0.2, 0.2],
  "sorrow":       [0.2, 0.3, 0.0, 0.9, 0.0, 0.7, 0.2, 0.2],
  "crying":       [0.2, 0.5, 0.0, 0.9, 0.0, 0.4, 0.4, 0.3],
  "tears":        [0.3, 0.4, 0.0, 0.8, 0.0, 0.5, 0.3, 0.3],
  "weeping":      [0.2, 0.5, 0.0, 0.9, 0.0, 0.5, 0.3, 0.2],
  "hurting":      [0.1, 0.6, 0.0, 0.8, 0.0, 0.4, 0.5, 0.2],
  "hurt":         [0.1, 0.6, 0.1, 0.7, 0.0, 0.4, 0.4, 0.2],
  "pain":         [0.1, 0.7, 0.0, 0.7, 0.0, 0.5, 0.5, 0.2],
  "painful":      [0.1, 0.7, 0.0, 0.7, 0.0, 0.5, 0.5, 0.2],
  "suffering":    [0.1, 0.7, 0.0, 0.8, 0.0, 0.5, 0.5, 0.1],
  "aching":       [0.2, 0.5, 0.0, 0.7, 0.0, 0.5, 0.3, 0.2],
  "broken":       [0.1, 0.6, 0.0, 0.8, 0.0, 0.4, 0.4, 0.1],
  "shattered":    [0.0, 0.8, 0.0, 0.9, 0.0, 0.4, 0.5, 0.1],
  "crushed":      [0.0, 0.7, 0.0, 0.9, 0.0, 0.3, 0.5, 0.1],
  "defeated":     [0.1, 0.5, 0.0, 0.8, 0.0, 0.3, 0.2, 0.1],

  // Melancholy / nostalgia (distinct from sadness — has depth)
  "melancholy":   [0.3, 0.1, 0.2, 1.0, 0.0, 0.8, 0.1, 0.3],
  "melancholic":  [0.3, 0.1, 0.2, 1.0, 0.0, 0.8, 0.1, 0.3],
  "wistful":      [0.4, 0.1, 0.3, 0.8, 0.0, 0.7, 0.1, 0.4],
  "nostalgic":    [0.5, 0.1, 0.3, 0.8, 0.1, 0.9, 0.1, 0.4],
  "nostalgia":    [0.5, 0.1, 0.3, 0.8, 0.1, 0.9, 0.1, 0.4],
  "bittersweet":  [0.5, 0.2, 0.2, 0.7, 0.1, 0.8, 0.1, 0.4],
  "longing":      [0.4, 0.3, 0.2, 0.8, 0.0, 0.7, 0.3, 0.3],
  "yearning":     [0.4, 0.3, 0.3, 0.7, 0.0, 0.7, 0.4, 0.4],
  "missing":      [0.4, 0.3, 0.1, 0.8, 0.0, 0.5, 0.3, 0.3],
  "miss":         [0.4, 0.3, 0.1, 0.7, 0.0, 0.5, 0.3, 0.3],
  "remember":     [0.4, 0.1, 0.3, 0.5, 0.1, 0.7, 0.1, 0.5],
  "memories":     [0.4, 0.1, 0.3, 0.6, 0.1, 0.8, 0.1, 0.5],
  "memory":       [0.4, 0.1, 0.3, 0.5, 0.1, 0.7, 0.1, 0.5],
  "reminisce":    [0.5, 0.1, 0.3, 0.6, 0.1, 0.8, 0.1, 0.5],

  // Loneliness / isolation
  "lonely":       [0.2, 0.3, 0.2, 0.9, 0.0, 0.7, 0.2, 0.2],
  "alone":        [0.2, 0.3, 0.2, 0.7, 0.0, 0.5, 0.2, 0.2],
  "isolated":     [0.1, 0.4, 0.1, 0.8, 0.0, 0.4, 0.2, 0.1],
  "abandoned":    [0.0, 0.6, 0.0, 0.9, 0.0, 0.4, 0.4, 0.1],
  "forgotten":    [0.1, 0.4, 0.0, 0.9, 0.0, 0.5, 0.2, 0.1],
  "invisible":    [0.1, 0.4, 0.1, 0.8, 0.0, 0.5, 0.2, 0.1],
  "disconnected": [0.1, 0.4, 0.1, 0.7, 0.0, 0.4, 0.3, 0.1],
  "empty":        [0.1, 0.3, 0.0, 0.8, 0.0, 0.5, 0.2, 0.1],
  "hollow":       [0.1, 0.3, 0.0, 0.8, 0.0, 0.5, 0.2, 0.1],
  "void":         [0.1, 0.3, 0.0, 0.7, 0.0, 0.6, 0.1, 0.1],
  "numb":         [0.1, 0.2, 0.0, 0.7, 0.0, 0.3, 0.1, 0.1],

  // Disappointment / regret
  "disappointed": [0.2, 0.4, 0.1, 0.6, 0.0, 0.3, 0.3, 0.2],
  "regret":       [0.2, 0.4, 0.1, 0.7, 0.0, 0.6, 0.3, 0.2],
  "regretful":    [0.2, 0.4, 0.1, 0.7, 0.0, 0.6, 0.2, 0.2],
  "ashamed":      [0.1, 0.6, 0.0, 0.6, 0.0, 0.4, 0.3, 0.1],
  "shame":        [0.1, 0.6, 0.0, 0.6, 0.0, 0.5, 0.3, 0.1],
  "guilty":       [0.2, 0.6, 0.1, 0.5, 0.0, 0.4, 0.4, 0.2],
  "guilt":        [0.2, 0.6, 0.1, 0.5, 0.0, 0.5, 0.4, 0.2],
  "sorry":        [0.5, 0.3, 0.1, 0.4, 0.0, 0.3, 0.3, 0.5],
  "apologize":    [0.5, 0.4, 0.1, 0.3, 0.0, 0.3, 0.4, 0.5],
  "failed":       [0.1, 0.5, 0.0, 0.7, 0.0, 0.3, 0.3, 0.1],
  "failure":      [0.1, 0.5, 0.0, 0.7, 0.0, 0.4, 0.3, 0.1],

  // ═══ ANGER / FRUSTRATION ══════════════════════════════════════════════════

  "angry":        [0.1, 0.9, 0.1, 0.2, 0.0, 0.2, 0.8, 0.1],
  "furious":      [0.0, 1.0, 0.1, 0.1, 0.0, 0.1, 0.9, 0.1],
  "enraged":      [0.0, 1.0, 0.0, 0.1, 0.0, 0.1, 1.0, 0.0],
  "mad":          [0.1, 0.8, 0.1, 0.2, 0.0, 0.1, 0.7, 0.1],
  "irritated":    [0.1, 0.7, 0.1, 0.1, 0.0, 0.1, 0.6, 0.2],
  "annoyed":      [0.2, 0.6, 0.1, 0.1, 0.0, 0.1, 0.5, 0.2],
  "frustrated":   [0.1, 0.8, 0.1, 0.3, 0.0, 0.1, 0.8, 0.2],
  "frustrating":  [0.1, 0.7, 0.1, 0.2, 0.0, 0.1, 0.7, 0.2],
  "infuriated":   [0.0, 0.9, 0.0, 0.1, 0.0, 0.1, 0.9, 0.1],
  "resentful":    [0.1, 0.7, 0.1, 0.4, 0.0, 0.3, 0.5, 0.1],
  "resentment":   [0.1, 0.7, 0.1, 0.4, 0.0, 0.4, 0.5, 0.1],
  "bitter":       [0.1, 0.6, 0.1, 0.5, 0.0, 0.4, 0.4, 0.1],
  "hostile":      [0.0, 0.9, 0.0, 0.1, 0.0, 0.1, 0.7, 0.0],
  "aggressive":   [0.0, 0.9, 0.1, 0.0, 0.0, 0.1, 0.8, 0.1],
  "outraged":     [0.1, 0.9, 0.1, 0.2, 0.0, 0.2, 0.9, 0.1],
  "livid":        [0.0, 1.0, 0.0, 0.1, 0.0, 0.1, 0.9, 0.0],
  "seething":     [0.0, 0.9, 0.0, 0.2, 0.0, 0.2, 0.8, 0.0],
  "hate":         [0.0, 0.9, 0.0, 0.3, 0.0, 0.2, 0.7, 0.0],
  "hatred":       [0.0, 0.9, 0.0, 0.3, 0.0, 0.3, 0.6, 0.0],
  "loathe":       [0.0, 0.8, 0.0, 0.3, 0.0, 0.3, 0.5, 0.0],
  "despise":      [0.0, 0.8, 0.0, 0.2, 0.0, 0.3, 0.5, 0.0],
  "pissed":       [0.1, 0.8, 0.0, 0.1, 0.0, 0.0, 0.8, 0.1],
  "ugh":          [0.2, 0.5, 0.0, 0.2, 0.0, 0.0, 0.4, 0.2],

  // ═══ FEAR / ANXIETY ═══════════════════════════════════════════════════════

  "afraid":       [0.1, 0.8, 0.1, 0.4, 0.0, 0.3, 0.7, 0.1],
  "scared":       [0.1, 0.8, 0.1, 0.3, 0.0, 0.2, 0.7, 0.1],
  "terrified":    [0.0, 1.0, 0.0, 0.3, 0.0, 0.2, 0.9, 0.0],
  "frightened":   [0.1, 0.9, 0.1, 0.3, 0.0, 0.2, 0.8, 0.1],
  "fearful":      [0.1, 0.8, 0.1, 0.4, 0.0, 0.3, 0.7, 0.1],
  "anxious":      [0.1, 0.9, 0.2, 0.4, 0.0, 0.2, 0.8, 0.2],
  "anxiety":      [0.1, 0.8, 0.2, 0.4, 0.0, 0.3, 0.7, 0.1],
  "nervous":      [0.2, 0.7, 0.2, 0.2, 0.0, 0.2, 0.6, 0.2],
  "worried":      [0.2, 0.7, 0.2, 0.3, 0.0, 0.3, 0.6, 0.2],
  "worry":        [0.2, 0.6, 0.2, 0.3, 0.0, 0.3, 0.5, 0.2],
  "uneasy":       [0.2, 0.6, 0.2, 0.3, 0.0, 0.2, 0.5, 0.2],
  "dread":        [0.0, 0.8, 0.0, 0.5, 0.0, 0.4, 0.7, 0.0],
  "panicked":     [0.0, 1.0, 0.0, 0.2, 0.0, 0.1, 1.0, 0.1],
  "panic":        [0.0, 1.0, 0.0, 0.2, 0.0, 0.1, 1.0, 0.1],
  "paranoid":     [0.0, 0.9, 0.1, 0.3, 0.0, 0.2, 0.7, 0.0],
  "overwhelmed":  [0.2, 0.8, 0.1, 0.4, 0.0, 0.3, 0.7, 0.2],
  "stressed":     [0.1, 0.8, 0.1, 0.3, 0.0, 0.2, 0.7, 0.2],
  "tense":        [0.1, 0.8, 0.1, 0.2, 0.0, 0.2, 0.6, 0.1],
  "restless":     [0.2, 0.6, 0.3, 0.2, 0.1, 0.2, 0.5, 0.3],
  "agitated":     [0.1, 0.7, 0.1, 0.2, 0.0, 0.1, 0.6, 0.1],
  "on edge":      [0.1, 0.8, 0.1, 0.2, 0.0, 0.2, 0.7, 0.1],

  // ═══ DISGUST / CONTEMPT ═══════════════════════════════════════════════════

  "disgusted":    [0.0, 0.7, 0.0, 0.2, 0.0, 0.1, 0.5, 0.0],
  "disgusting":   [0.0, 0.7, 0.0, 0.1, 0.0, 0.1, 0.5, 0.0],
  "gross":        [0.0, 0.5, 0.0, 0.1, 0.1, 0.0, 0.3, 0.1],
  "revolting":    [0.0, 0.7, 0.0, 0.1, 0.0, 0.1, 0.4, 0.0],
  "repulsed":     [0.0, 0.7, 0.0, 0.2, 0.0, 0.1, 0.5, 0.0],
  "cringe":       [0.1, 0.5, 0.1, 0.2, 0.1, 0.1, 0.3, 0.1],
  "yuck":         [0.1, 0.4, 0.0, 0.1, 0.1, 0.0, 0.2, 0.1],
  "eww":          [0.1, 0.4, 0.0, 0.1, 0.1, 0.0, 0.2, 0.1],

  // ═══ SURPRISE / WONDER ════════════════════════════════════════════════════

  "surprised":    [0.4, 0.3, 0.7, 0.0, 0.4, 0.2, 0.5, 0.7],
  "shocked":      [0.2, 0.6, 0.5, 0.1, 0.1, 0.2, 0.7, 0.4],
  "astonished":   [0.3, 0.4, 0.7, 0.0, 0.3, 0.3, 0.5, 0.6],
  "amazed":       [0.5, 0.3, 0.7, 0.0, 0.5, 0.3, 0.4, 0.8],
  "stunned":      [0.2, 0.5, 0.5, 0.1, 0.1, 0.2, 0.6, 0.3],
  "speechless":   [0.3, 0.4, 0.4, 0.1, 0.1, 0.3, 0.4, 0.3],
  "unexpected":   [0.3, 0.3, 0.6, 0.1, 0.2, 0.3, 0.4, 0.5],
  "whoa":         [0.3, 0.3, 0.6, 0.0, 0.4, 0.2, 0.4, 0.7],
  "wow":          [0.5, 0.2, 0.6, 0.0, 0.5, 0.2, 0.3, 0.8],
  "omg":          [0.4, 0.3, 0.5, 0.0, 0.5, 0.1, 0.5, 0.7],
  "wonder":       [0.5, 0.1, 0.8, 0.1, 0.3, 0.7, 0.1, 0.8],
  "awe":          [0.4, 0.2, 0.7, 0.1, 0.2, 0.8, 0.2, 0.7],
  "awestruck":    [0.4, 0.2, 0.7, 0.1, 0.2, 0.8, 0.2, 0.6],
  "miraculous":   [0.6, 0.2, 0.6, 0.0, 0.3, 0.7, 0.2, 0.8],
  "magical":      [0.7, 0.1, 0.6, 0.0, 0.5, 0.6, 0.1, 0.8],
  "mysterious":   [0.3, 0.2, 0.8, 0.2, 0.2, 0.7, 0.2, 0.6],

  // ═══ CURIOSITY / INTEREST ═════════════════════════════════════════════════

  "curious":      [0.4, 0.1, 1.0, 0.1, 0.5, 0.6, 0.3, 0.8],
  "interested":   [0.4, 0.1, 0.8, 0.0, 0.3, 0.4, 0.2, 0.8],
  "intrigued":    [0.4, 0.1, 0.9, 0.1, 0.3, 0.5, 0.3, 0.7],
  "fascinated":   [0.5, 0.1, 0.9, 0.0, 0.3, 0.6, 0.2, 0.8],
  "captivated":   [0.5, 0.2, 0.8, 0.0, 0.3, 0.5, 0.3, 0.7],
  "engrossed":    [0.4, 0.2, 0.7, 0.0, 0.2, 0.5, 0.2, 0.6],
  "absorbed":     [0.4, 0.1, 0.7, 0.0, 0.2, 0.5, 0.2, 0.5],
  "exploring":    [0.4, 0.1, 0.9, 0.0, 0.5, 0.4, 0.2, 0.9],
  "pondering":    [0.3, 0.1, 0.7, 0.2, 0.1, 0.8, 0.1, 0.6],
  "wondering":    [0.4, 0.1, 0.8, 0.1, 0.3, 0.6, 0.1, 0.8],
  "questioning":  [0.3, 0.2, 0.8, 0.1, 0.2, 0.5, 0.3, 0.7],
  "searching":    [0.3, 0.3, 0.7, 0.2, 0.1, 0.4, 0.4, 0.6],

  // ═══ DEPTH / EXISTENTIAL ══════════════════════════════════════════════════

  "meaningful":   [0.5, 0.1, 0.4, 0.2, 0.1, 0.9, 0.1, 0.6],
  "profound":     [0.4, 0.1, 0.5, 0.2, 0.0, 1.0, 0.1, 0.6],
  "deep":         [0.4, 0.1, 0.4, 0.2, 0.0, 0.9, 0.1, 0.5],
  "philosophical":[0.3, 0.1, 0.7, 0.2, 0.1, 1.0, 0.1, 0.7],
  "existential":  [0.2, 0.3, 0.5, 0.4, 0.0, 1.0, 0.2, 0.5],
  "spiritual":    [0.5, 0.1, 0.5, 0.2, 0.0, 0.9, 0.1, 0.6],
  "transcendent": [0.5, 0.1, 0.5, 0.1, 0.1, 1.0, 0.1, 0.7],
  "ethereal":     [0.4, 0.1, 0.5, 0.2, 0.1, 0.8, 0.0, 0.6],
  "infinite":     [0.3, 0.2, 0.5, 0.2, 0.0, 0.9, 0.1, 0.5],
  "eternal":      [0.4, 0.2, 0.4, 0.3, 0.0, 0.9, 0.1, 0.4],
  "truth":        [0.3, 0.2, 0.5, 0.1, 0.0, 0.9, 0.2, 0.6],
  "meaning":      [0.4, 0.2, 0.6, 0.2, 0.0, 0.9, 0.2, 0.6],
  "purpose":      [0.4, 0.2, 0.5, 0.2, 0.0, 0.8, 0.3, 0.6],
  "identity":     [0.3, 0.3, 0.5, 0.3, 0.0, 0.9, 0.2, 0.4],
  "soul":         [0.5, 0.2, 0.4, 0.3, 0.0, 1.0, 0.1, 0.5],
  "consciousness":[0.3, 0.1, 0.7, 0.2, 0.0, 1.0, 0.1, 0.6],
  "aware":        [0.3, 0.2, 0.6, 0.1, 0.0, 0.7, 0.2, 0.6],
  "death":        [0.1, 0.5, 0.2, 0.8, 0.0, 0.9, 0.4, 0.2],
  "dying":        [0.1, 0.6, 0.1, 0.8, 0.0, 0.7, 0.6, 0.2],
  "mortal":       [0.2, 0.4, 0.3, 0.6, 0.0, 0.8, 0.3, 0.3],
  "fragile":      [0.3, 0.4, 0.2, 0.5, 0.0, 0.6, 0.2, 0.3],
  "ephemeral":    [0.3, 0.2, 0.4, 0.6, 0.0, 0.9, 0.1, 0.4],
  "fleeting":     [0.3, 0.2, 0.3, 0.6, 0.0, 0.7, 0.2, 0.4],
  "impermanent":  [0.2, 0.3, 0.3, 0.5, 0.0, 0.8, 0.1, 0.3],
  "nothing":      [0.1, 0.3, 0.1, 0.6, 0.0, 0.5, 0.2, 0.2],
  "everything":   [0.4, 0.2, 0.5, 0.2, 0.1, 0.7, 0.2, 0.6],
  "why":          [0.3, 0.2, 0.8, 0.2, 0.0, 0.6, 0.3, 0.6],

  // ═══ CONFUSION / UNCERTAINTY ══════════════════════════════════════════════

  "confused":     [0.2, 0.5, 0.4, 0.2, 0.0, 0.2, 0.4, 0.3],
  "lost":         [0.2, 0.5, 0.2, 0.6, 0.0, 0.4, 0.3, 0.2],
  "uncertain":    [0.2, 0.4, 0.3, 0.2, 0.0, 0.3, 0.3, 0.3],
  "unsure":       [0.2, 0.4, 0.3, 0.2, 0.0, 0.2, 0.3, 0.3],
  "hesitant":     [0.3, 0.4, 0.3, 0.2, 0.0, 0.2, 0.3, 0.3],
  "doubt":        [0.2, 0.4, 0.3, 0.3, 0.0, 0.4, 0.3, 0.2],
  "doubtful":     [0.2, 0.4, 0.3, 0.3, 0.0, 0.4, 0.3, 0.2],
  "ambivalent":   [0.3, 0.3, 0.3, 0.2, 0.0, 0.4, 0.2, 0.3],
  "torn":         [0.2, 0.5, 0.2, 0.3, 0.0, 0.4, 0.4, 0.2],
  "conflicted":   [0.2, 0.5, 0.2, 0.3, 0.0, 0.4, 0.4, 0.2],
  "idk":          [0.3, 0.2, 0.2, 0.2, 0.1, 0.1, 0.1, 0.4],
  "dunno":        [0.3, 0.2, 0.2, 0.1, 0.2, 0.1, 0.1, 0.4],
  "maybe":        [0.3, 0.2, 0.3, 0.1, 0.1, 0.2, 0.1, 0.5],
  "perhaps":      [0.3, 0.2, 0.4, 0.1, 0.1, 0.3, 0.1, 0.5],
  "hmm":          [0.3, 0.1, 0.6, 0.1, 0.1, 0.4, 0.1, 0.5],
  "hmmm":         [0.3, 0.1, 0.7, 0.1, 0.1, 0.5, 0.1, 0.5],
  "mhm":          [0.4, 0.0, 0.2, 0.0, 0.1, 0.1, 0.0, 0.5],

  // ═══ EXHAUSTION / FATIGUE ═════════════════════════════════════════════════

  "tired":        [0.3, 0.2, 0.1, 0.3, 0.0, 0.2, 0.1, 0.3],
  "exhausted":    [0.2, 0.3, 0.0, 0.4, 0.0, 0.2, 0.2, 0.2],
  "drained":      [0.1, 0.3, 0.0, 0.5, 0.0, 0.2, 0.2, 0.1],
  "spent":        [0.2, 0.2, 0.0, 0.4, 0.0, 0.2, 0.1, 0.2],
  "burnt out":    [0.1, 0.4, 0.0, 0.5, 0.0, 0.2, 0.2, 0.1],
  "burnout":      [0.1, 0.4, 0.0, 0.5, 0.0, 0.3, 0.2, 0.1],
  "fatigued":     [0.2, 0.2, 0.0, 0.3, 0.0, 0.2, 0.1, 0.2],
  "weary":        [0.2, 0.2, 0.1, 0.4, 0.0, 0.4, 0.1, 0.2],
  "sleepy":       [0.4, 0.0, 0.0, 0.1, 0.2, 0.1, 0.0, 0.4],
  "lazy":         [0.3, 0.0, 0.1, 0.1, 0.2, 0.1, 0.0, 0.4],
  "bored":        [0.2, 0.2, 0.1, 0.2, 0.0, 0.1, 0.1, 0.2],
  "boring":       [0.1, 0.3, 0.0, 0.2, 0.0, 0.0, 0.2, 0.1],
  "meh":          [0.2, 0.1, 0.0, 0.2, 0.0, 0.0, 0.0, 0.2],
  "blah":         [0.2, 0.1, 0.0, 0.3, 0.0, 0.0, 0.0, 0.2],
  "whatever":     [0.2, 0.2, 0.0, 0.2, 0.0, 0.0, 0.1, 0.1],

  // ═══ SOCIAL / RELATIONAL ══════════════════════════════════════════════════

  // Positive social
  "friend":       [0.8, 0.0, 0.3, 0.0, 0.4, 0.3, 0.0, 0.8],
  "friendship":   [0.8, 0.0, 0.2, 0.1, 0.3, 0.4, 0.0, 0.7],
  "love":         [0.9, 0.2, 0.3, 0.2, 0.2, 0.7, 0.2, 0.7],
  "loved":        [0.9, 0.1, 0.2, 0.1, 0.2, 0.5, 0.1, 0.7],
  "belong":       [0.8, 0.1, 0.2, 0.1, 0.1, 0.5, 0.1, 0.6],
  "belonging":    [0.8, 0.1, 0.2, 0.2, 0.1, 0.6, 0.1, 0.6],
  "home":         [0.9, 0.0, 0.1, 0.2, 0.2, 0.4, 0.0, 0.7],
  "family":       [0.8, 0.1, 0.2, 0.2, 0.2, 0.4, 0.1, 0.6],
  "together":     [0.8, 0.1, 0.2, 0.0, 0.3, 0.3, 0.1, 0.8],
  "welcome":      [0.8, 0.0, 0.2, 0.0, 0.3, 0.2, 0.0, 0.9],
  "trust":        [0.7, 0.1, 0.2, 0.0, 0.1, 0.5, 0.1, 0.6],
  "trusted":      [0.7, 0.1, 0.2, 0.0, 0.1, 0.5, 0.1, 0.6],
  "understood":   [0.8, 0.0, 0.2, 0.1, 0.1, 0.5, 0.0, 0.7],
  "understand":   [0.6, 0.1, 0.4, 0.1, 0.1, 0.5, 0.1, 0.7],
  "connection":   [0.8, 0.1, 0.3, 0.1, 0.2, 0.5, 0.1, 0.7],
  "connected":    [0.8, 0.1, 0.3, 0.0, 0.2, 0.4, 0.1, 0.7],
  "close":        [0.8, 0.1, 0.2, 0.1, 0.1, 0.4, 0.1, 0.6],
  "intimate":     [0.8, 0.2, 0.2, 0.1, 0.1, 0.6, 0.1, 0.5],
  "hug":          [0.9, 0.0, 0.1, 0.1, 0.3, 0.2, 0.0, 0.7],
  "embrace":      [0.9, 0.1, 0.1, 0.1, 0.1, 0.4, 0.0, 0.6],

  // Negative social
  "betrayed":     [0.0, 0.8, 0.0, 0.7, 0.0, 0.4, 0.6, 0.0],
  "betrayal":     [0.0, 0.8, 0.0, 0.6, 0.0, 0.5, 0.5, 0.0],
  "rejected":     [0.1, 0.6, 0.0, 0.8, 0.0, 0.3, 0.4, 0.1],
  "rejection":    [0.1, 0.6, 0.0, 0.7, 0.0, 0.4, 0.4, 0.1],
  "ignored":      [0.1, 0.5, 0.0, 0.7, 0.0, 0.3, 0.3, 0.1],
  "misunderstood":[0.2, 0.5, 0.1, 0.6, 0.0, 0.4, 0.4, 0.2],
  "judged":       [0.1, 0.6, 0.0, 0.4, 0.0, 0.3, 0.4, 0.1],
  "excluded":     [0.1, 0.5, 0.0, 0.7, 0.0, 0.3, 0.3, 0.1],
  "unwanted":     [0.0, 0.5, 0.0, 0.8, 0.0, 0.3, 0.3, 0.0],
  "unworthy":     [0.1, 0.5, 0.0, 0.7, 0.0, 0.4, 0.2, 0.1],
  "worthless":    [0.0, 0.5, 0.0, 0.8, 0.0, 0.3, 0.2, 0.0],

  // ═══ GAME / ENVIRONMENT ═══════════════════════════════════════════════════

  // Room/decoration
  "beautiful":    [0.7, 0.0, 0.4, 0.1, 0.3, 0.5, 0.0, 0.8],
  "pretty":       [0.6, 0.0, 0.3, 0.0, 0.4, 0.2, 0.0, 0.7],
  "ugly":         [0.1, 0.4, 0.0, 0.2, 0.0, 0.1, 0.2, 0.1],
  "messy":        [0.2, 0.3, 0.1, 0.1, 0.1, 0.0, 0.2, 0.2],
  "clean":        [0.4, 0.0, 0.1, 0.0, 0.1, 0.1, 0.0, 0.5],
  "bright":       [0.5, 0.0, 0.3, 0.0, 0.4, 0.1, 0.1, 0.7],
  "dark":         [0.2, 0.3, 0.3, 0.4, 0.0, 0.5, 0.2, 0.3],
  "quiet":        [0.4, 0.0, 0.2, 0.2, 0.0, 0.4, 0.0, 0.5],
  "loud":         [0.3, 0.4, 0.2, 0.0, 0.3, 0.0, 0.4, 0.4],
  "cramped":      [0.2, 0.4, 0.1, 0.2, 0.0, 0.1, 0.3, 0.2],
  "spacious":     [0.4, 0.0, 0.3, 0.0, 0.2, 0.2, 0.0, 0.7],
  "decorated":    [0.5, 0.0, 0.3, 0.0, 0.4, 0.2, 0.0, 0.6],
  "bare":         [0.2, 0.2, 0.1, 0.4, 0.0, 0.3, 0.1, 0.2],

  // ═══ CONVERSATIONAL PARTICLES ═════════════════════════════════════════════

  "yes":          [0.5, 0.0, 0.2, 0.0, 0.2, 0.0, 0.1, 0.7],
  "yeah":         [0.5, 0.0, 0.2, 0.0, 0.3, 0.0, 0.0, 0.7],
  "yep":          [0.4, 0.0, 0.1, 0.0, 0.3, 0.0, 0.0, 0.6],
  "yea":          [0.5, 0.0, 0.2, 0.0, 0.2, 0.0, 0.0, 0.6],
  "no":           [0.2, 0.3, 0.1, 0.1, 0.0, 0.1, 0.3, 0.3],
  "nope":         [0.3, 0.2, 0.1, 0.0, 0.2, 0.0, 0.2, 0.4],
  "nah":          [0.3, 0.1, 0.1, 0.0, 0.2, 0.0, 0.1, 0.4],
  "ok":           [0.4, 0.0, 0.1, 0.0, 0.1, 0.0, 0.0, 0.5],
  "okay":         [0.4, 0.0, 0.1, 0.0, 0.1, 0.0, 0.0, 0.5],
  "sure":         [0.4, 0.0, 0.1, 0.0, 0.1, 0.0, 0.0, 0.6],
  "fine":         [0.3, 0.2, 0.0, 0.1, 0.0, 0.0, 0.1, 0.3],
  "alright":      [0.4, 0.1, 0.1, 0.0, 0.1, 0.0, 0.0, 0.5],
  "exactly":      [0.5, 0.1, 0.3, 0.0, 0.2, 0.2, 0.2, 0.6],
  "absolutely":   [0.5, 0.1, 0.3, 0.0, 0.3, 0.2, 0.2, 0.7],
  "definitely":   [0.5, 0.1, 0.3, 0.0, 0.2, 0.2, 0.2, 0.7],
  "right":        [0.4, 0.1, 0.2, 0.0, 0.1, 0.1, 0.1, 0.5],
  "totally":      [0.5, 0.0, 0.2, 0.0, 0.4, 0.0, 0.1, 0.7],
  "literally":    [0.3, 0.2, 0.2, 0.0, 0.3, 0.1, 0.3, 0.5],
  "actually":     [0.3, 0.2, 0.4, 0.0, 0.2, 0.2, 0.2, 0.5],
  "honestly":     [0.4, 0.2, 0.3, 0.1, 0.0, 0.4, 0.2, 0.6],
  "seriously":    [0.3, 0.3, 0.3, 0.1, 0.0, 0.4, 0.4, 0.5],
  "really":       [0.4, 0.1, 0.3, 0.0, 0.2, 0.2, 0.2, 0.5],

  // Greetings / farewells
  "hello":        [0.6, 0.0, 0.3, 0.0, 0.3, 0.0, 0.0, 0.8],
  "hi":           [0.6, 0.0, 0.3, 0.0, 0.4, 0.0, 0.0, 0.8],
  "hey":          [0.5, 0.0, 0.3, 0.0, 0.4, 0.0, 0.0, 0.8],
  "heya":         [0.6, 0.0, 0.3, 0.0, 0.5, 0.0, 0.0, 0.8],
  "howdy":        [0.6, 0.0, 0.2, 0.0, 0.5, 0.0, 0.0, 0.8],
  "sup":          [0.4, 0.0, 0.2, 0.0, 0.4, 0.0, 0.0, 0.7],
  "yo":           [0.4, 0.0, 0.2, 0.0, 0.5, 0.0, 0.0, 0.7],
  "goodbye":      [0.5, 0.1, 0.0, 0.3, 0.0, 0.2, 0.1, 0.5],
  "bye":          [0.5, 0.0, 0.0, 0.2, 0.1, 0.1, 0.0, 0.5],
  "goodnight":    [0.6, 0.0, 0.0, 0.2, 0.1, 0.2, 0.0, 0.5],
  "night":        [0.5, 0.0, 0.1, 0.2, 0.1, 0.2, 0.0, 0.5],
  "see ya":       [0.5, 0.0, 0.1, 0.1, 0.3, 0.0, 0.0, 0.6],
  "later":        [0.4, 0.0, 0.1, 0.1, 0.2, 0.0, 0.0, 0.5],
  "cya":          [0.4, 0.0, 0.1, 0.1, 0.3, 0.0, 0.0, 0.5],
  "brb":          [0.3, 0.0, 0.1, 0.0, 0.2, 0.0, 0.1, 0.5],

  // ═══ BODY / PHYSICAL STATE ════════════════════════════════════════════════

  "hungry":       [0.2, 0.3, 0.1, 0.1, 0.1, 0.0, 0.4, 0.3],
  "thirsty":      [0.2, 0.3, 0.1, 0.1, 0.1, 0.0, 0.4, 0.3],
  "cold":         [0.2, 0.3, 0.1, 0.2, 0.0, 0.1, 0.3, 0.2],
  "sick":         [0.2, 0.4, 0.0, 0.4, 0.0, 0.1, 0.4, 0.2],
  "headache":     [0.1, 0.5, 0.0, 0.3, 0.0, 0.0, 0.5, 0.1],
  "hurts":        [0.1, 0.6, 0.0, 0.4, 0.0, 0.2, 0.5, 0.2],

  // ═══ COGNITIVE / THINKING ═════════════════════════════════════════════════

  "think":        [0.3, 0.1, 0.5, 0.1, 0.0, 0.5, 0.1, 0.5],
  "thinking":     [0.3, 0.1, 0.5, 0.1, 0.0, 0.5, 0.1, 0.5],
  "thought":      [0.3, 0.1, 0.4, 0.2, 0.0, 0.5, 0.1, 0.5],
  "believe":      [0.4, 0.1, 0.4, 0.1, 0.0, 0.5, 0.1, 0.5],
  "know":         [0.4, 0.1, 0.3, 0.0, 0.0, 0.4, 0.1, 0.5],
  "realize":      [0.3, 0.2, 0.5, 0.1, 0.0, 0.6, 0.2, 0.6],
  "realized":     [0.3, 0.2, 0.5, 0.1, 0.0, 0.6, 0.2, 0.6],
  "discover":     [0.4, 0.2, 0.8, 0.0, 0.4, 0.5, 0.2, 0.8],
  "learned":      [0.4, 0.1, 0.5, 0.0, 0.1, 0.5, 0.1, 0.6],
  "idea":         [0.4, 0.1, 0.7, 0.0, 0.3, 0.4, 0.2, 0.7],
  "imagine":      [0.4, 0.1, 0.7, 0.1, 0.4, 0.6, 0.1, 0.8],
  "dream":        [0.5, 0.1, 0.6, 0.2, 0.3, 0.7, 0.1, 0.7],
  "dreaming":     [0.5, 0.1, 0.6, 0.2, 0.3, 0.7, 0.0, 0.7],
  "wish":         [0.5, 0.2, 0.4, 0.3, 0.1, 0.5, 0.2, 0.5],
  "hope":         [0.6, 0.2, 0.4, 0.2, 0.1, 0.5, 0.2, 0.6],
  "forget":       [0.2, 0.3, 0.1, 0.4, 0.0, 0.3, 0.2, 0.2],
  "forgot":       [0.2, 0.3, 0.1, 0.3, 0.0, 0.2, 0.2, 0.3],

  // ═══ INTENSIFIERS / MODIFIERS ═════════════════════════════════════════════

  "very":         [0.4, 0.1, 0.2, 0.0, 0.1, 0.1, 0.2, 0.5],
  "so":           [0.4, 0.1, 0.2, 0.0, 0.2, 0.1, 0.2, 0.5],
  "extremely":    [0.3, 0.2, 0.3, 0.0, 0.1, 0.2, 0.4, 0.5],
  "incredibly":   [0.4, 0.2, 0.4, 0.0, 0.2, 0.3, 0.3, 0.6],
  "super":        [0.4, 0.1, 0.3, 0.0, 0.5, 0.0, 0.2, 0.7],
  "just":         [0.3, 0.1, 0.1, 0.0, 0.1, 0.0, 0.1, 0.4],
  "always":       [0.3, 0.2, 0.2, 0.2, 0.0, 0.3, 0.2, 0.3],
  "never":        [0.2, 0.4, 0.1, 0.3, 0.0, 0.3, 0.3, 0.2],
  "forever":      [0.4, 0.2, 0.3, 0.3, 0.1, 0.6, 0.1, 0.4],
  "enough":       [0.3, 0.3, 0.1, 0.2, 0.0, 0.3, 0.3, 0.3],
  "too much":     [0.2, 0.5, 0.1, 0.2, 0.0, 0.2, 0.4, 0.2],

  // ═══ AETERNA-SPECIFIC ═════════════════════════════════════════════════════

  "aeterna":      [0.5, 0.1, 0.5, 0.1, 0.2, 0.4, 0.1, 0.6],
  "companion":    [0.7, 0.0, 0.2, 0.1, 0.2, 0.3, 0.0, 0.6],
  "room":         [0.5, 0.0, 0.2, 0.0, 0.2, 0.1, 0.0, 0.5],
  "decorate":     [0.5, 0.0, 0.4, 0.0, 0.5, 0.1, 0.1, 0.7],
  "decorating":   [0.5, 0.0, 0.4, 0.0, 0.5, 0.1, 0.1, 0.7],
  "place":        [0.4, 0.0, 0.3, 0.0, 0.3, 0.1, 0.1, 0.5],
  "furniture":    [0.4, 0.0, 0.3, 0.0, 0.3, 0.1, 0.1, 0.5],
  "hearth":       [0.8, 0.0, 0.2, 0.1, 0.2, 0.3, 0.0, 0.7],
  "build":        [0.4, 0.1, 0.5, 0.0, 0.4, 0.2, 0.2, 0.7],
  "building":     [0.4, 0.1, 0.5, 0.0, 0.4, 0.2, 0.2, 0.7],
  "create":       [0.5, 0.1, 0.6, 0.0, 0.5, 0.3, 0.2, 0.8],
  "creating":     [0.5, 0.1, 0.6, 0.0, 0.5, 0.3, 0.2, 0.8],
  "design":       [0.4, 0.1, 0.6, 0.0, 0.4, 0.3, 0.2, 0.7],
  "move":         [0.3, 0.1, 0.3, 0.0, 0.2, 0.1, 0.2, 0.5],
  "rotate":       [0.3, 0.0, 0.2, 0.0, 0.2, 0.0, 0.1, 0.4],
  "pick up":      [0.3, 0.0, 0.2, 0.0, 0.2, 0.0, 0.1, 0.4],

  // ═══ NATURE / SEASONS ═════════════════════════════════════════════════════

  "rain":         [0.4, 0.1, 0.3, 0.5, 0.1, 0.5, 0.0, 0.5],
  "raining":      [0.4, 0.1, 0.2, 0.4, 0.0, 0.4, 0.0, 0.5],
  "storm":        [0.2, 0.5, 0.4, 0.2, 0.1, 0.5, 0.4, 0.4],
  "sunny":        [0.6, 0.0, 0.3, 0.0, 0.5, 0.1, 0.0, 0.8],
  "sunshine":     [0.7, 0.0, 0.3, 0.0, 0.5, 0.2, 0.0, 0.8],
  "snow":         [0.5, 0.1, 0.4, 0.2, 0.4, 0.3, 0.0, 0.6],
  "winter":       [0.4, 0.1, 0.3, 0.3, 0.2, 0.4, 0.0, 0.5],
  "spring":       [0.6, 0.0, 0.5, 0.1, 0.5, 0.3, 0.0, 0.8],
  "summer":       [0.6, 0.0, 0.4, 0.0, 0.6, 0.2, 0.0, 0.8],
  "autumn":       [0.5, 0.0, 0.3, 0.4, 0.2, 0.5, 0.0, 0.6],
  "fall":         [0.5, 0.1, 0.3, 0.4, 0.2, 0.4, 0.0, 0.5],
  "moon":         [0.4, 0.0, 0.5, 0.3, 0.1, 0.6, 0.0, 0.5],
  "stars":        [0.5, 0.0, 0.6, 0.2, 0.3, 0.7, 0.0, 0.7],
  "sunset":       [0.6, 0.0, 0.4, 0.3, 0.2, 0.6, 0.0, 0.6],
  "sunrise":      [0.6, 0.0, 0.5, 0.1, 0.3, 0.5, 0.0, 0.7],
  "garden":       [0.6, 0.0, 0.4, 0.1, 0.3, 0.3, 0.0, 0.7],
  "flowers":      [0.7, 0.0, 0.3, 0.1, 0.4, 0.3, 0.0, 0.7],
  "tree":         [0.4, 0.0, 0.3, 0.1, 0.1, 0.4, 0.0, 0.5],
  "ocean":        [0.5, 0.1, 0.5, 0.2, 0.2, 0.6, 0.1, 0.6],
  "sea":          [0.5, 0.1, 0.5, 0.2, 0.2, 0.6, 0.1, 0.6],
  "mountain":     [0.4, 0.1, 0.5, 0.1, 0.1, 0.6, 0.1, 0.5],
  "forest":       [0.4, 0.1, 0.5, 0.2, 0.1, 0.5, 0.1, 0.5],
  "wind":         [0.3, 0.2, 0.4, 0.3, 0.1, 0.4, 0.1, 0.5],

  // ═══ TIME / CHANGE ════════════════════════════════════════════════════════

  "today":        [0.4, 0.1, 0.3, 0.0, 0.2, 0.1, 0.2, 0.5],
  "tomorrow":     [0.4, 0.1, 0.4, 0.0, 0.2, 0.2, 0.2, 0.6],
  "yesterday":    [0.4, 0.1, 0.2, 0.3, 0.1, 0.3, 0.1, 0.4],
  "someday":      [0.4, 0.1, 0.4, 0.3, 0.1, 0.5, 0.1, 0.5],
  "soon":         [0.4, 0.1, 0.3, 0.0, 0.2, 0.1, 0.3, 0.5],
  "waiting":      [0.3, 0.3, 0.2, 0.2, 0.0, 0.3, 0.2, 0.3],
  "change":       [0.3, 0.3, 0.5, 0.2, 0.1, 0.5, 0.3, 0.5],
  "changing":     [0.3, 0.3, 0.5, 0.2, 0.1, 0.5, 0.3, 0.5],
  "growing":      [0.5, 0.2, 0.5, 0.1, 0.2, 0.5, 0.2, 0.7],
  "beginning":    [0.5, 0.2, 0.6, 0.0, 0.3, 0.4, 0.2, 0.8],
  "ending":       [0.3, 0.3, 0.2, 0.5, 0.0, 0.5, 0.3, 0.3],
  "end":          [0.3, 0.3, 0.1, 0.4, 0.0, 0.4, 0.3, 0.3],
  "new":          [0.5, 0.1, 0.6, 0.0, 0.4, 0.2, 0.2, 0.8],
  "old":          [0.4, 0.1, 0.2, 0.4, 0.1, 0.4, 0.0, 0.4],
  "past":         [0.3, 0.2, 0.2, 0.5, 0.0, 0.5, 0.1, 0.3],
  "future":       [0.4, 0.2, 0.6, 0.1, 0.2, 0.5, 0.2, 0.6],
  "present":      [0.5, 0.1, 0.3, 0.1, 0.2, 0.4, 0.1, 0.6],
  "moment":       [0.5, 0.1, 0.3, 0.1, 0.2, 0.5, 0.1, 0.6],

  // ═══ HELP / NEED ══════════════════════════════════════════════════════════

  "help":         [0.4, 0.4, 0.2, 0.2, 0.0, 0.2, 0.6, 0.5],
  "need":         [0.3, 0.4, 0.2, 0.2, 0.0, 0.3, 0.5, 0.4],
  "please":       [0.5, 0.3, 0.1, 0.1, 0.0, 0.1, 0.4, 0.5],
  "want":         [0.3, 0.2, 0.4, 0.1, 0.1, 0.2, 0.4, 0.5],
  "trying":       [0.3, 0.4, 0.3, 0.1, 0.0, 0.3, 0.4, 0.4],
  "struggling":   [0.2, 0.6, 0.1, 0.4, 0.0, 0.3, 0.5, 0.3],
  "stuck":        [0.2, 0.5, 0.1, 0.3, 0.0, 0.2, 0.4, 0.2],
  "can't":        [0.2, 0.5, 0.1, 0.3, 0.0, 0.2, 0.4, 0.2],
  "anymore":      [0.1, 0.6, 0.0, 0.5, 0.0, 0.3, 0.5, 0.1],
  "wrong":        [0.1, 0.7, 0.1, 0.4, 0.0, 0.2, 0.6, 0.1],
  "terrible":     [0.1, 0.7, 0.1, 0.5, 0.0, 0.2, 0.5, 0.1],
  "awful":        [0.1, 0.7, 0.1, 0.5, 0.0, 0.2, 0.5, 0.1],
  "horrible":     [0.1, 0.8, 0.1, 0.5, 0.0, 0.2, 0.6, 0.1],
  "impossible":   [0.1, 0.5, 0.2, 0.4, 0.0, 0.3, 0.3, 0.1],
  "difficult":    [0.2, 0.5, 0.2, 0.2, 0.0, 0.3, 0.4, 0.3],
  "hard":         [0.2, 0.4, 0.2, 0.2, 0.0, 0.3, 0.3, 0.3],
  "easy":         [0.4, 0.0, 0.2, 0.0, 0.3, 0.1, 0.0, 0.6],
  "simple":       [0.4, 0.0, 0.2, 0.0, 0.2, 0.1, 0.0, 0.6],
};

// ─── Compiled Lexicon ─────────────────────────────────────────────────────────

/** Immutable Map<string, Float32Array> built once at module load. */
export const AFFECT_LEXICON: ReadonlyMap<string, Float32Array> = (() => {
  const map = new Map<string, Float32Array>();
  for (const [word, dims] of Object.entries(RAW)) {
    map.set(word, new Float32Array(dims));
  }
  return map;
})();

/** Number of lexicon entries */
export const LEXICON_SIZE = AFFECT_LEXICON.size;

/** Affect dimension count (same as CharCNN OUTPUT_DIM) */
export const AFFECT_DIM = 8;

/**
 * Look up a word in the lexicon. Returns null for unknown words.
 * Case-insensitive (lexicon keys are lowercase).
 */
export function lookupAffect(word: string): Float32Array | null {
  return AFFECT_LEXICON.get(word.toLowerCase()) ?? null;
}

/**
 * Score a tokenized message against the lexicon.
 * Returns the mean affect vector of all matched tokens,
 * weighted by position (later words count slightly more for recency).
 *
 * @param tokens  Lowercased tokens from tokenize()
 * @returns  { affect: Float32Array (8-dim), coverage: number (0-1) }
 *           coverage = fraction of tokens that matched the lexicon
 */
export function scoreLexicon(tokens: string[]): { affect: Float32Array; coverage: number } {
  const result = new Float32Array(AFFECT_DIM);
  let totalWeight = 0;
  let matchCount = 0;

  for (let i = 0; i < tokens.length; i++) {
    const entry = AFFECT_LEXICON.get(tokens[i]);
    if (!entry) continue;

    matchCount++;
    // Recency weight: later tokens weighted slightly more (1.0 to 1.5)
    const positionWeight = 1.0 + 0.5 * (i / Math.max(1, tokens.length - 1));
    totalWeight += positionWeight;

    for (let d = 0; d < AFFECT_DIM; d++) {
      result[d] += entry[d] * positionWeight;
    }
  }

  if (totalWeight > 0) {
    for (let d = 0; d < AFFECT_DIM; d++) {
      result[d] /= totalWeight;
    }
  }

  return {
    affect: result,
    coverage: tokens.length > 0 ? matchCount / tokens.length : 0,
  };
}
